export type TerritoryId =
  | 'brand'
  | 'innovation'
  | 'packaging'
  | 'advertising'
  | 'positioning'
  | 'trend'
  | 'pricing'
  | 'audience'
  | 'other';

export type BudgetFeel =
  | 'very_tight'
  | 'tight'
  | 'comfortable'
  | 'stretch'
  | 'significant'
  | '';

export type ResultSolidity = 'directional' | 'decision_grade' | 'board_grade' | '';

export interface Practicalities {
  budget: BudgetFeel;
  budgetUnknown: boolean;
  decisionDate: string;
  noFixedDate: boolean;
  geography: string;
  crossBorders: boolean;
  solidity: ResultSolidity;
  buyer: string;
  locked: string;
}

export interface BriefBuilderAnswers {
  decision: string;
  territory: TerritoryId | '';
  territoryLabel: string;
  honestQuestion: string;
  people: string;
  whatYouKnow: string;
  insiderFact: string;
  territoryQ1: string;
  territoryQ2: string;
  brandImpact: string;
  leastSure: string;
  dataHeld: string;
  ideas: string;
  ideasDetail: string;
  optional: string;
  practicalities: Practicalities;
  /** Step ids marked as unknown */
  unknowns: string[];
}

export type WizardPhase =
  | 'intro'
  | 'question'
  | 'mid_check'
  | 'final_readback'
  | 'generating'
  | 'done';

export interface TerritoryOption {
  id: TerritoryId;
  title: string;
  description: string;
}

export interface TerritoryQuestions {
  q1Label: string;
  q1Prompt: string;
  q1Hint: string;
  q2Label: string;
  q2Prompt: string;
  q2Hint: string;
}

export interface TextStepDef {
  id: string;
  number: string;
  label: string;
  kind: 'text' | 'territory' | 'choice' | 'when' | 'practicalities';
  prompt: string;
  hint: string;
  answerKey: keyof BriefBuilderAnswers | 'practicalities';
  allowUnknown?: boolean;
  optional?: boolean;
  encouragement?: string;
  example?: string;
  placeholder?: string;
  addon?: { key: 'leastSure' | 'dataHeld'; label: string; placeholder: string };
  choices?: { value: string; label: string; hint: string }[];
}

export function emptyAnswers(): BriefBuilderAnswers {
  return {
    decision: '',
    territory: '',
    territoryLabel: '',
    honestQuestion: '',
    people: '',
    whatYouKnow: '',
    insiderFact: '',
    territoryQ1: '',
    territoryQ2: '',
    brandImpact: '',
    leastSure: '',
    dataHeld: '',
    ideas: '',
    ideasDetail: '',
    optional: '',
    practicalities: {
      budget: '',
      budgetUnknown: false,
      decisionDate: '',
      noFixedDate: false,
      geography: '',
      crossBorders: false,
      solidity: '',
      buyer: '',
      locked: '',
    },
    unknowns: [],
  };
}

export function practicalitiesSummary(p: Practicalities): string {
  const parts: string[] = [];
  if (p.budgetUnknown) parts.push('Budget unknown');
  else if (p.budget) {
    const labels: Record<string, string> = {
      very_tight: 'Very tight',
      tight: 'Tight',
      comfortable: 'Comfortable',
      stretch: 'A stretch',
      significant: 'Significant investment',
    };
    parts.push(labels[p.budget] || p.budget);
  }
  if (p.noFixedDate) parts.push('No fixed date');
  else if (p.decisionDate) parts.push(p.decisionDate);
  if (p.buyer) parts.push(p.buyer);
  if (p.geography) parts.push(p.geography);
  if (p.solidity === 'directional') parts.push('Directional');
  if (p.solidity === 'decision_grade') parts.push('Decision-grade');
  if (p.solidity === 'board_grade') parts.push('Board-grade');
  if (p.locked) parts.push(p.locked);
  return parts.join(' · ') || '';
}

export function sidebarValue(
  answers: BriefBuilderAnswers,
  key: string,
  draft?: string
): string {
  if (draft !== undefined && draft !== '') return draft;
  if (answers.unknowns.includes(key)) return 'Unknown — research will find out';
  switch (key) {
    case 'decision':
      return answers.decision;
    case 'territory':
      return answers.territoryLabel;
    case 'honest_question':
      return answers.honestQuestion;
    case 'people':
      return answers.people;
    case 'what_you_know':
      return answers.whatYouKnow;
    case 'insider_fact':
      return answers.insiderFact;
    case 'territory_q1':
      return answers.territoryQ1;
    case 'territory_q2':
      return answers.territoryQ2;
    case 'brand_impact':
      return answers.brandImpact;
    case 'ideas':
      return [answers.ideas, answers.ideasDetail].filter(Boolean).join(' — ');
    case 'optional':
      return answers.optional;
    case 'practicalities':
      return practicalitiesSummary(answers.practicalities);
    default:
      return '';
  }
}
